import {
  Add,
  CopyFile,
  DocumentView,
  ViewOff,
  ChevronLeft,
  ChevronRight,
  SysProvision,
  Draggable,
} from '@carbon/react/icons';
import { createMutationAtom } from 'atoms/create-resource-atom';
import {
  Button,
  Column,
  FlexGrid as Grid,
  Row,
  Tile,
  Loading,
  InlineNotification,
} from '@carbon/react';
import { EmptyState } from 'components/empty-state';
import { Default } from 'components/icons';
import { InterpretationForm } from 'components/interpretation-form';
import { interpretationDuplicateAtom } from 'components/interpretation-form/atoms';
import { InterpretationSearchModal } from 'components/interpretation-search';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  useState,
  useCallback,
  useRef,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import {
  Redirect,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import {
  DocumentInterpretationResponse,
  DocumentResponse,
} from 'types/api-responses';
import {
  documentProvisionStore,
  showProvisionedDocumentAtom,
  useInterpretationsAtom,
} from '../common/atoms';
import type { DocumentPageProps } from '../common/header';
import {
  EmptyInterpretationCard,
  InterpretationCard,
  InterpretationCardSkeleton,
} from './interpretation-card';
import plur from 'plur';
import classNames from 'classnames';
import style from './page.module.scss';
import { Route } from 'components/route';
import { isImageInlineRenderable } from 'utils/document-image';
import { Helmet } from 'react-helmet-async';
import { addSearchParamToReferrer } from '../common/utils';
import {
  showDocumentFilePreferenceAtom,
  showInterpretationListPreferenceAtom,
} from './atoms';
import * as ProvisionedDocument from 'components/provisioned-document';
import { useBodyClass } from 'lib/hooks/useBodyClass';

const createDuplicateAtom = createMutationAtom<
  DocumentInterpretationResponse,
  'duplicateInterpretation'
>();

const RESIZABLE_INITIAL_WIDTH = 50;
const RESIZABLE_MIN_WIDTH = 20;
const RESIZABLE_MAX_WIDTH = 80;

const PageContent = ({
  document,
  getDocument,
}: {
  document: DocumentResponse;
  getDocument: () => Promise<void>;
}) => {
  const [width, setWidth] = useState(RESIZABLE_INITIAL_WIDTH);
  const isDragging = useRef(false);

  const dragHandler = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if (!isDragging.current) return;

      const parentRect = e.currentTarget.getBoundingClientRect();
      const w = ((e.clientX - parentRect.left) / parentRect.width) * 100;

      if (w >= RESIZABLE_MIN_WIDTH && w <= RESIZABLE_MAX_WIDTH && w !== width) {
        setWidth(w);
      }
    },
    [width]
  );

  const startDrag = () => {
    isDragging.current = true;
  };

  const stopDrag = () => {
    isDragging.current = false;
  };

  const interpretationsAtom = useInterpretationsAtom(
    document?.resources.interpretations.href
  );
  let { loading, data: interpretations } = useAtomValue(interpretationsAtom);
  const refetchInterpretations = useSetAtom(interpretationsAtom);
  const setInterpretationDuplicate = useSetAtom(interpretationDuplicateAtom);
  const runRequest = useSetAtom(createDuplicateAtom);

  const location = useLocation();
  const history = useHistory();
  const { path } = useRouteMatch();

  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);

  const [showDocument, setShowDocument] = useAtom(
    showDocumentFilePreferenceAtom
  );

  const [isInterpretationListVisible, setInterpretationListVisible] = useAtom(
    showInterpretationListPreferenceAtom
  );

  const [showProvisionedDocument, setShowProvisionedDocument] = useAtom(
    showProvisionedDocumentAtom
  );

  if (!document) {
    return null;
  }

  const documentImage = document.resources.image;
  const documentImageInlineRenderable = isImageInlineRenderable(
    documentImage?.contentType
  );

  if (interpretations && 'error' in interpretations) {
    return (
      <InlineNotification
        kind="error"
        title={interpretations.error}
        lowContrast
      />
    );
  }

  const params = new URLSearchParams(location.search);
  const searchParams = params.get('referrer')
    ? `?referrer=${params.get('referrer')}`
    : '';

  interpretations = interpretations || [];

  return (
    <Grid className="bx--no-gutter" fullWidth condensed>
      <Row>
        <Column
          md={isInterpretationListVisible ? 0 : 2}
          lg={isInterpretationListVisible ? 0 : 3}
          as="nav"
        >
          <Tile className={style.masthead}>
            <header>
              <div className={style.btnsArea}>
                <Button
                  renderIcon={Add}
                  size="md"
                  kind="tertiary"
                  className={style.btnHeader}
                  onClick={() => {
                    history.replace({
                      pathname: document.newInterpretationHref,
                      search: searchParams,
                    });
                  }}
                >
                  <span>New</span>
                </Button>
                <InterpretationSearchModal
                  title="Choose an Interpretation to Duplicate"
                  isOpen={duplicateModalOpen}
                  onClose={() => setDuplicateModalOpen(false)}
                  actions={(interpretation) => (
                    <Button
                      renderIcon={CopyFile}
                      className={style.copyBtn}
                      kind="ghost"
                      size="sm"
                      onClick={() => {
                        runRequest({
                          type: 'POST',
                          url: document.resources.duplicateInterpretations.href,
                          data: {
                            interpretation: {
                              id: interpretation.id,
                            },
                          },
                          onSuccess: (
                            interpretationDuplicate: DocumentInterpretationResponse
                          ) => {
                            setInterpretationDuplicate(interpretationDuplicate);
                            history.replace({
                              pathname: `${document.newInterpretationHref}`,
                              search: addSearchParamToReferrer(
                                location.search,
                                'duplicateId',
                                interpretation.id.toString()
                              ),
                            });
                            setDuplicateModalOpen(false);
                          },
                        });
                      }}
                    >
                      Copy from...
                    </Button>
                  )}
                />
                <Button
                  renderIcon={CopyFile}
                  kind="ghost"
                  size="md"
                  className={style.btnHeader}
                  tooltipAlignment="center"
                  tooltipPosition="bottom"
                  iconDescription="Copy from..."
                  hasIconOnly
                  onClick={() => setDuplicateModalOpen(true)}
                />
                <Button
                  renderIcon={showDocument ? ViewOff : DocumentView}
                  kind="ghost"
                  size="md"
                  className={style.btnHeader}
                  tooltipAlignment="center"
                  tooltipPosition="bottom"
                  iconDescription={
                    showDocument ? 'Hide Document' : 'Show Document'
                  }
                  hasIconOnly
                  onClick={() => {
                    setShowDocument(!showDocument);
                  }}
                />
                <Button
                  renderIcon={showProvisionedDocument ? ViewOff : SysProvision}
                  kind="ghost"
                  size="md"
                  className={style.btnHeader}
                  tooltipAlignment="center"
                  tooltipPosition="bottom"
                  iconDescription={
                    showProvisionedDocument
                      ? 'Hide Provisioned Document'
                      : 'Show Provisioned Document'
                  }
                  hasIconOnly
                  onClick={() => {
                    setShowProvisionedDocument((c) => !c);
                  }}
                />
              </div>
            </header>
            <h2 className={style.interpretationCount}>
              {`(${document.interpretationsCount}) `}
              {plur('Interpretation', document.interpretationsCount)}
            </h2>
          </Tile>
          {loading
            ? [...Array(document.interpretationsCount).keys()].map((el) => (
                <InterpretationCardSkeleton key={el} />
              ))
            : interpretations.map((el, i) => (
                <InterpretationCard
                  key={el.id}
                  interpretation={el}
                  index={i}
                  searchParams={searchParams}
                />
              ))}
          <Switch>
            <Route exact path={`${path}/new`}>
              <EmptyInterpretationCard index={interpretations.length} />
            </Route>
          </Switch>
        </Column>
        <Column
          md={isInterpretationListVisible ? 16 : 6}
          lg={isInterpretationListVisible ? 16 : 13}
          className={style.loadOverlay}
        >
          <Row
            className={style.resizableGrid}
            onMouseMove={dragHandler}
            onMouseUp={stopDrag}
            onMouseLeave={stopDrag}
          >
            <div
              style={{
                width:
                  showProvisionedDocument || showDocument
                    ? `${width}%`
                    : `100%`,
              }}
            >
              <div className={style.closeAreabtn}>
                <Button
                  renderIcon={
                    isInterpretationListVisible ? ChevronRight : ChevronLeft
                  }
                  kind="ghost"
                  hasIconOnly
                  iconDescription={
                    isInterpretationListVisible
                      ? `Show Interpretation List`
                      : `Hide Interpretation List`
                  }
                  tooltipAlignment="start"
                  tooltipPosition="top"
                  size="sm"
                  className={style.closePanelBtn}
                  onClick={() => {
                    setInterpretationListVisible(!isInterpretationListVisible);
                  }}
                />
              </div>
              <Switch>
                <Route exact path={`${path}/`}>
                  {!interpretations.length ? (
                    !loading ? (
                      <Tile className={style.emptyStateCol}>
                        <EmptyState
                          IconComponent={Default}
                          headerText="No Interpretations"
                          helperText="Interpretations would normally display here, but none have been added yet."
                        />
                      </Tile>
                    ) : (
                      <Loading small withOverlay />
                    )
                  ) : (
                    <Redirect
                      to={`${interpretations[0].href}${location.search}`}
                    />
                  )}
                </Route>
                <Route exact path={`${path}/new`}>
                  <InterpretationForm
                    key={location.search}
                    document={document}
                    interpretations={interpretations}
                    role={{
                      type: 'create',
                      href: document.resources.interpretations.href,
                    }}
                    refetchInterpretations={() => {
                      refetchInterpretations();
                      getDocument();
                    }}
                  />
                </Route>
                <Route exact path={`${path}/:id`}>
                  <InterpretationForm
                    key={location.pathname}
                    document={document}
                    interpretations={interpretations}
                    role={{ type: 'update', href: location.pathname }}
                    refetchInterpretations={() => {
                      refetchInterpretations();
                      getDocument();
                    }}
                  />
                </Route>
              </Switch>
            </div>

            {showProvisionedDocument || showDocument ? (
              <div className={style.dragHandle} onMouseDown={startDrag}>
                <Draggable />
              </div>
            ) : null}

            {showProvisionedDocument && (
              <div
                className={classNames(style.provisionedFrame, style.dragColumn)}
              >
                <div className={style.sticky}>
                  <ProvisionedDocument.List
                    store={documentProvisionStore}
                    onProvisionsAvailable={() => {}}
                  />
                </div>
              </div>
            )}
            {showDocument && (
              <div
                className={classNames(style.documentFrame, style.dragColumn)}
              >
                {documentImage && (
                  <div className={style.sticky}>
                    {documentImageInlineRenderable ? (
                      <iframe src={documentImage.href} />
                    ) : (
                      <div className={style.downloadOnly}>
                        <p>{documentImage.filename ?? 'Document file'}</p>
                        <Button
                          kind="primary"
                          href={documentImage.href}
                          target="_blank"
                          rel="noopener"
                        >
                          Download
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </Row>
        </Column>
      </Row>
    </Grid>
  );
};

// Content only — the document fetch and DocumentHeader live in
// DocumentEntrypointPage so they survive a tab switch.
const InterpretationsPage = ({ document, getDocument }: DocumentPageProps) => {
  useBodyClass('interpretationPage', true);

  return (
    <>
      <Helmet>
        <title>{`${document.documentTitle} - Interpretations`}</title>
      </Helmet>
      <PageContent document={document} getDocument={getDocument} />
    </>
  );
};

export { InterpretationsPage };
